import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  Cog6ToothIcon,
  EnvelopeOpenIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { PMButton } from "../../utils/PMButton";
import {
  createCompanyAiMailbox,
  deleteCompanyAiMailbox,
  disconnectCompanyAiMailboxMicrosoft,
  fetchCompanyAiMailboxes,
  fetchCompanyAiMailboxMailFolders,
  fetchCompanyAiMailboxMessages,
  getMicrosoftAuthorizeUrl,
  postCompanyAiMailboxProcessMessages,
  postCompanyAiMailboxSendTestMail,
} from "../../../api/microsoft";
import {
  MICROSOFT_OAUTH_INTENT_KEY,
  MICROSOFT_OAUTH_RETURN_PATH_KEY,
} from "../../../utils/microsoftOAuth";
import { getAccessToken } from "../../../slices/session";
import { CompanyAiMailboxSettingsPanel } from "./CompanyAiMailboxSettingsPanel";
import { CompanyAiMailboxMessageReaderDialog } from "./CompanyAiMailboxMessageReaderDialog";

/** @type {string} */
const DEFAULT_MAIL_FOLDER_KEY = "wk:inbox";

/** Well-known Graph mail folder segments (see API `well_known_folder`). */
const WELL_KNOWN_MAIL_FOLDER_KEYS = [
  "wk:inbox",
  "wk:archive",
  "wk:sentitems",
  "wk:deleteditems",
  "wk:junkemail",
  "wk:drafts",
  "wk:outbox",
];

/**
 * @param {string} [key] e.g. wk:inbox, id:{graphFolderId}
 * @returns {{ wellKnownFolder?: string, mailFolderId?: string }}
 */
function mailFolderKeyToApiParams(key) {
  const k = (key || DEFAULT_MAIL_FOLDER_KEY).trim();
  if (k.startsWith("id:")) {
    const graphId = k.slice(3).trim();
    return graphId ? { mailFolderId: graphId } : { wellKnownFolder: "inbox" };
  }
  if (k.startsWith("wk:")) {
    const seg = k.slice(3).trim().toLowerCase();
    return seg ? { wellKnownFolder: seg } : { wellKnownFolder: "inbox" };
  }
  return { wellKnownFolder: "inbox" };
}

/**
 * Admin: shared company addresses + Microsoft-delegated link per mailbox (Graph inbox + test send).
 */
export function CompanyAiMailboxesSection() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  /** @type {[number[], React.Dispatch<React.SetStateAction<number[]>>]} */
  const [expandedMailboxIds, setExpandedMailboxIds] = useState([]);
  const [settingsOpenId, setSettingsOpenId] = useState(null);
  const [messagesById, setMessagesById] = useState({});
  const [loadingMessagesId, setLoadingMessagesId] = useState(null);
  const [mailFoldersByMailboxId, setMailFoldersByMailboxId] = useState({});
  const [loadingFoldersId, setLoadingFoldersId] = useState(null);
  /** @type {[Record<number, string>, React.Dispatch<React.SetStateAction<Record<number, string>>>]} */
  const [selectedFolderKeyByMailboxId, setSelectedFolderKeyByMailboxId] = useState({});
  /** @type {[Record<number, string[]>, Function]} */
  const [selectedMessageIdsByMailbox, setSelectedMessageIdsByMailbox] = useState({});
  const [processingMailboxId, setProcessingMailboxId] = useState(null);
  /** @type {[{ mailboxId: number, mailboxEmail: string, graphMessageId: string, subject: string } | null, Function]} */
  const [readerTarget, setReaderTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetchCompanyAiMailboxes()
      .then((data) => setItems(data?.ai_mailboxes || []))
      .catch(() => {
        setItems([]);
        toast.error(t("microsoft.ai_mailboxes.load_failed"));
      })
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMailFolders = async (mailboxId) => {
    setLoadingFoldersId(mailboxId);
    try {
      const data = await fetchCompanyAiMailboxMailFolders(mailboxId);
      const raw = data?.mail_folders || [];
      const rows = raw.map((f) => ({
        id: f.id,
        display_name: f.display_name ?? f.displayName ?? "(folder)",
        parent_folder_id: f.parent_folder_id ?? f.parentFolderId,
        child_folder_count: f.child_folder_count ?? f.childFolderCount ?? 0,
        depth: f.depth ?? 0,
      }));
      setMailFoldersByMailboxId((prev) => ({ ...prev, [mailboxId]: rows }));
    } catch (err) {
      const msg =
        err?.response?.data?.errors?.[0] ||
        t("microsoft.ai_mailboxes.folder.folders_failed");
      toast.error(msg);
      setMailFoldersByMailboxId((prev) => ({ ...prev, [mailboxId]: [] }));
    } finally {
      setLoadingFoldersId(null);
    }
  };

  const loadMessages = async (id, folderKeyOverride) => {
    const key =
      folderKeyOverride ??
      selectedFolderKeyByMailboxId[id] ??
      DEFAULT_MAIL_FOLDER_KEY;
    const folderOpts = mailFolderKeyToApiParams(key);
    setLoadingMessagesId(id);
    try {
      const data = await fetchCompanyAiMailboxMessages(id, {
        top: 25,
        ...folderOpts,
      });
      setMessagesById((prev) => ({
        ...prev,
        [id]: data?.messages || [],
      }));
      setSelectedMessageIdsByMailbox((prev) => ({ ...prev, [id]: [] }));
    } catch (err) {
      const msg =
        err?.response?.data?.errors?.[0] ||
        t("microsoft.ai_mailboxes.messages_failed");
      toast.error(msg);
      setMessagesById((prev) => ({ ...prev, [id]: [] }));
    } finally {
      setLoadingMessagesId(null);
    }
  };

  const mergeMailboxInList = (updated) => {
    if (!updated?.id) return;
    setItems((prev) =>
      prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x))
    );
    if (expandedMailboxIds.includes(updated.id) && updated.microsoft_connected) {
      const fk =
        selectedFolderKeyByMailboxId[updated.id] || DEFAULT_MAIL_FOLDER_KEY;
      loadMessages(updated.id, fk);
    }
  };

  const toggleInbox = (row) => {
    const isOpen = expandedMailboxIds.includes(row.id);
    if (isOpen) {
      setExpandedMailboxIds((prev) => prev.filter((x) => x !== row.id));
      return;
    }
    setExpandedMailboxIds((prev) => [...prev, row.id]);
    if (row.microsoft_connected) {
      loadMailFolders(row.id);
      loadMessages(row.id);
    }
  };

  const toggleSettings = (id) => {
    setSettingsOpenId((prev) => (prev === id ? null : id));
  };

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error(t("microsoft.ai_mailboxes.email_required"));
      return;
    }
    setBusy(true);
    try {
      await createCompanyAiMailbox({ email_address: trimmed });
      toast.success(t("microsoft.ai_mailboxes.added"));
      setEmail("");
      load();
    } catch (err) {
      const msg =
        err?.response?.data?.errors?.[0] || t("microsoft.ai_mailboxes.add_failed");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    setBusy(true);
    try {
      await deleteCompanyAiMailbox(id);
      toast.success(t("microsoft.ai_mailboxes.removed"));
      setExpandedMailboxIds((prev) => prev.filter((x) => x !== id));
      setMailFoldersByMailboxId((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setSelectedFolderKeyByMailboxId((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      if (settingsOpenId === id) setSettingsOpenId(null);
      load();
    } catch {
      toast.error(t("microsoft.ai_mailboxes.remove_failed"));
    } finally {
      setBusy(false);
    }
  };

  const linkMicrosoft = async (row) => {
    const token = getAccessToken();
    if (!token) {
      toast.error(t("microsoft.toast.login_required"));
      return;
    }
    try {
      const data = await getMicrosoftAuthorizeUrl("company_ai_mailbox", {
        company_ai_mailbox_id: row.id,
      });
      const url = data?.url;
      if (!url) {
        toast.error(t("microsoft.toast.start_failed"));
        return;
      }
      sessionStorage.setItem(MICROSOFT_OAUTH_INTENT_KEY, "company_ai_mailbox");
      sessionStorage.setItem(
        MICROSOFT_OAUTH_RETURN_PATH_KEY,
        window.location.pathname
      );
      window.location.assign(url);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 503 || status === 422) {
        toast.error(
          e?.response?.data?.errors?.[0] || t("microsoft.toast.missing_env")
        );
      } else {
        toast.error(t("microsoft.toast.start_failed"));
      }
    }
  };

  const handleDisconnectMicrosoft = async (id) => {
    setBusy(true);
    try {
      await disconnectCompanyAiMailboxMicrosoft(id);
      toast.success(t("microsoft.ai_mailboxes.microsoft_disconnected"));
      setMessagesById((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setExpandedMailboxIds((prev) => prev.filter((x) => x !== id));
      setMailFoldersByMailboxId((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setSelectedFolderKeyByMailboxId((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      if (settingsOpenId === id) setSettingsOpenId(null);
      load();
    } catch {
      toast.error(t("microsoft.ai_mailboxes.microsoft_disconnect_failed"));
    } finally {
      setBusy(false);
    }
  };

  const handleSendTest = async (id) => {
    setBusy(true);
    try {
      const data = await postCompanyAiMailboxSendTestMail(id);
      toast.success(data?.message || t("microsoft.ai_mailboxes.shared_test_ok"));
    } catch (err) {
      const msg =
        err?.response?.data?.errors?.[0] ||
        t("microsoft.ai_mailboxes.shared_test_failed");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const toggleMessageSelected = (mailboxId, messageId) => {
    if (!messageId) return;
    setSelectedMessageIdsByMailbox((prev) => {
      const cur = prev[mailboxId] || [];
      const has = cur.includes(messageId);
      const next = has ? cur.filter((x) => x !== messageId) : [...cur, messageId];
      return { ...prev, [mailboxId]: next };
    });
  };

  const selectAllMessages = (mailboxId, msgs) => {
    if (!msgs?.length) return;
    const all = msgs.map((m) => m.id).filter(Boolean);
    setSelectedMessageIdsByMailbox((prev) => ({ ...prev, [mailboxId]: all }));
  };

  const clearMessageSelection = (mailboxId) => {
    setSelectedMessageIdsByMailbox((prev) => ({ ...prev, [mailboxId]: [] }));
  };

  const summarizeProcessing = (inboxProcessing) => {
    const list = inboxProcessing?.messages || [];
    let forwardsSent = 0;
    let forwardsSim = 0;
    let skips = 0;
    list.forEach((m) => {
      (m.actions || []).forEach((a) => {
        if (a.type === "skip_case" && a.applied) skips += 1;
        if (a.type === "forward_to") {
          if (a.dry_run) forwardsSim += 1;
          else if (a.sent) forwardsSent += 1;
        }
      });
    });
    return { count: list.length, forwardsSent, forwardsSim, skips };
  };

  const runProcessMessages = async (mailboxId, dryRun) => {
    const ids = selectedMessageIdsByMailbox[mailboxId] || [];
    if (!ids.length) {
      toast.error(t("microsoft.ai_mailboxes.process_need_selection"));
      return;
    }
    if (!dryRun) {
      const ok = window.confirm(t("microsoft.ai_mailboxes.process_run_confirm"));
      if (!ok) return;
    }
    setProcessingMailboxId(mailboxId);
    try {
      const data = await postCompanyAiMailboxProcessMessages(mailboxId, {
        graph_message_ids: ids,
        dry_run: dryRun,
      });
      const ip = data?.inbox_processing;
      const s = summarizeProcessing(ip);
      if (dryRun) {
        toast.success(
          t("microsoft.ai_mailboxes.process_ok_simulated", {
            count: s.count,
            sim: s.forwardsSim,
            skips: s.skips,
          })
        );
      } else {
        toast.success(
          t("microsoft.ai_mailboxes.process_ok_executed", {
            count: s.count,
            sent: s.forwardsSent,
            skips: s.skips,
          })
        );
      }
    } catch (err) {
      const msg =
        err?.response?.data?.errors?.[0] ||
        t("microsoft.ai_mailboxes.process_failed");
      toast.error(msg);
    } finally {
      setProcessingMailboxId(null);
    }
  };

  return (
    <>
    <CompanyAiMailboxMessageReaderDialog
      open={Boolean(readerTarget)}
      onClose={() => setReaderTarget(null)}
      mailboxId={readerTarget?.mailboxId}
      mailboxEmail={readerTarget?.mailboxEmail}
      graphMessageId={readerTarget?.graphMessageId}
      previewSubject={readerTarget?.subject}
    />
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
      <h2 className="text-lg font-semibold text-gray-900">
        {t("microsoft.ai_mailboxes.title")}
      </h2>
      <p className="mt-1 text-sm text-gray-600">
        {t("microsoft.ai_mailboxes.intro")}
      </p>
      <p className="mt-2 rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-900 ring-1 ring-sky-100">
        {t("microsoft.ai_mailboxes.how_link_works")}
      </p>
      <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 ring-1 ring-amber-100">
        {t("microsoft.ai_mailboxes.mail_safety_dev_notice")}
      </p>
      <p className="mt-2 text-xs text-gray-600">{t("microsoft.ai_mailboxes.multi_inbox_hint")}</p>

      <form onSubmit={handleAdd} className="mt-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="min-w-0 w-full flex-1">
            <label
              htmlFor="company-ai-mailbox-email"
              className="block text-xs font-medium text-gray-700"
            >
              {t("microsoft.ai_mailboxes.email_label")}
            </label>
            <input
              id="company-ai-mailbox-email"
              type="email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              placeholder={t("microsoft.ai_mailboxes.email_placeholder")}
              className="mt-1 block h-10 w-full min-w-0 rounded-lg border border-gray-300 px-3 text-sm shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              disabled={busy || loading}
              autoComplete="off"
            />
          </div>
          <button
            type="submit"
            disabled={busy || loading}
            className="inline-flex h-10 shrink-0 items-center justify-center rounded-lg bg-sky-600 px-4 text-sm font-medium text-white shadow-sm hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-[5.5rem]"
          >
            {t("microsoft.ai_mailboxes.add")}
          </button>
        </div>
      </form>

      {loading ? (
        <p className="mt-4 text-sm text-gray-500">{t("microsoft.status.loading")}</p>
      ) : items.length === 0 ? (
        <p className="mt-4 text-sm text-gray-500">{t("microsoft.ai_mailboxes.empty")}</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {items.map((row) => {
            const open = expandedMailboxIds.includes(row.id);
            const msgs = messagesById[row.id];
            return (
              <li
                key={row.id}
                className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
              >
                <div className="flex flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-gray-900">
                      {row.email_address}
                    </p>
                    {row.microsoft_connected ? (
                      <p className="mt-0.5 text-xs text-emerald-700">
                        {t("microsoft.ai_mailboxes.linked_as", {
                          email: row.linked_microsoft_email || "—",
                        })}
                      </p>
                    ) : (
                      <p className="mt-0.5 text-xs text-amber-800">
                        {t("microsoft.ai_mailboxes.not_linked")}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {!row.microsoft_connected ? (
                      <PMButton
                        type="button"
                        variant="contained"
                        disabled={busy}
                        onClick={() => linkMicrosoft(row)}
                      >
                        {t("microsoft.ai_mailboxes.connect_microsoft")}
                      </PMButton>
                    ) : (
                      <>
                        <PMButton
                          type="button"
                          variant="outlined"
                          disabled={busy}
                          onClick={() => handleSendTest(row.id)}
                        >
                          {t("microsoft.ai_mailboxes.send_test")}
                        </PMButton>
                        <PMButton
                          type="button"
                          variant="outlined"
                          disabled={busy}
                          onClick={() => handleDisconnectMicrosoft(row.id)}
                        >
                          {t("microsoft.ai_mailboxes.disconnect_microsoft")}
                        </PMButton>
                      </>
                    )}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => toggleSettings(row.id)}
                      className="inline-flex items-center gap-1 rounded-lg border border-sky-200 bg-sky-50 px-2 py-1.5 text-xs font-medium text-sky-900 hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Cog6ToothIcon className="h-4 w-4 shrink-0" aria-hidden />
                      <span>{t("microsoft.ai_mailboxes.settings.toggle")}</span>
                    </button>
                    <button
                      type="button"
                      disabled={!row.microsoft_connected || busy}
                      onClick={() => toggleInbox(row)}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {open ? (
                        <ChevronUpIcon className="h-4 w-4" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4" />
                      )}
                      {t("microsoft.ai_mailboxes.show_inbox")}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(row.id)}
                      disabled={busy}
                      className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                      title={t("microsoft.ai_mailboxes.remove")}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                {settingsOpenId === row.id && (
                  <CompanyAiMailboxSettingsPanel
                    mailbox={row}
                    onSaved={mergeMailboxInList}
                  />
                )}
                {open && (
                  <div className="border-t border-gray-100 bg-gray-50/80 px-3 py-3">
                    {!row.microsoft_connected ? (
                      <p className="text-sm text-gray-600">
                        {t("microsoft.ai_mailboxes.inbox_requires_link")}
                      </p>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex flex-col gap-2 sm:max-w-xl">
                          <label
                            htmlFor={`ai-mailbox-folder-${row.id}`}
                            className="text-xs font-medium text-gray-700"
                          >
                            {t("microsoft.ai_mailboxes.folder.select_label")}
                          </label>
                          <select
                            id={`ai-mailbox-folder-${row.id}`}
                            value={
                              selectedFolderKeyByMailboxId[row.id] || DEFAULT_MAIL_FOLDER_KEY
                            }
                            onChange={(e) => {
                              const v = e.target.value;
                              setSelectedFolderKeyByMailboxId((p) => ({ ...p, [row.id]: v }));
                              loadMessages(row.id, v);
                            }}
                            disabled={busy || loadingMessagesId === row.id}
                            className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 disabled:bg-gray-100"
                          >
                            <optgroup label={t("microsoft.ai_mailboxes.folder.group_shortcuts")}>
                              {WELL_KNOWN_MAIL_FOLDER_KEYS.map((fk) => (
                                <option key={fk} value={fk}>
                                  {t(
                                    `microsoft.ai_mailboxes.folder.seg_${fk.replace("wk:", "")}`
                                  )}
                                </option>
                              ))}
                            </optgroup>
                            {(mailFoldersByMailboxId[row.id] || []).length > 0 ? (
                              <optgroup label={t("microsoft.ai_mailboxes.folder.group_tree")}>
                                {(mailFoldersByMailboxId[row.id] || []).map((f) => (
                                  <option key={f.id} value={`id:${f.id}`}>
                                    {"\u00A0\u00A0".repeat(
                                      Math.min(Number(f.depth) || 0, 12)
                                    )}
                                    {f.display_name}
                                  </option>
                                ))}
                              </optgroup>
                            ) : null}
                          </select>
                          {loadingFoldersId === row.id ? (
                            <p className="text-[11px] text-gray-500">
                              {t("microsoft.ai_mailboxes.folder.loading_tree")}
                            </p>
                          ) : null}
                        </div>
                        {loadingMessagesId === row.id ? (
                          <p className="text-sm text-gray-500">
                            {t("microsoft.status.loading")}
                          </p>
                        ) : !msgs || msgs.length === 0 ? (
                          <p className="text-sm text-gray-600">
                            {t("microsoft.ai_mailboxes.no_messages")}
                          </p>
                        ) : (
                      <div className="space-y-2">
                        <p className="text-xs text-gray-600">
                          {t("microsoft.ai_mailboxes.process_folder_hint")}
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            disabled={busy || processingMailboxId === row.id}
                            onClick={() => selectAllMessages(row.id, msgs)}
                            className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          >
                            {t("microsoft.ai_mailboxes.process_select_all")}
                          </button>
                          <button
                            type="button"
                            disabled={busy || processingMailboxId === row.id}
                            onClick={() => clearMessageSelection(row.id)}
                            className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          >
                            {t("microsoft.ai_mailboxes.process_clear_selection")}
                          </button>
                          <PMButton
                            type="button"
                            variant="outlined"
                            disabled={busy || processingMailboxId === row.id}
                            onClick={() => runProcessMessages(row.id, true)}
                          >
                            {t("microsoft.ai_mailboxes.process_simulate")}
                          </PMButton>
                          <PMButton
                            type="button"
                            variant="contained"
                            disabled={busy || processingMailboxId === row.id}
                            onClick={() => runProcessMessages(row.id, false)}
                          >
                            {t("microsoft.ai_mailboxes.process_run")}
                          </PMButton>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-left text-xs text-gray-800">
                            <thead>
                              <tr className="border-b border-gray-200 text-gray-500">
                                <th className="w-8 py-1 pr-1 font-medium">
                                  <span className="sr-only">
                                    {t("microsoft.ai_mailboxes.process_select_column")}
                                  </span>
                                </th>
                                <th className="py-1 pr-2 font-medium">
                                  {t("microsoft.ai_mailboxes.col.received")}
                                </th>
                                <th className="py-1 pr-2 font-medium">
                                  {t("microsoft.ai_mailboxes.col.from")}
                                </th>
                                <th className="py-1 font-medium">
                                  {t("microsoft.ai_mailboxes.col.subject")}
                                </th>
                                <th className="w-10 py-1 pl-1 text-right font-medium">
                                  <span className="sr-only">
                                    {t("microsoft.ai_mailboxes.reader.open_short")}
                                  </span>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {msgs.map((m) => {
                                const sel = (selectedMessageIdsByMailbox[row.id] || []).includes(m.id);
                                return (
                                  <tr key={m.id} className="border-b border-gray-100">
                                    <td className="py-1.5 pr-1 align-top">
                                      <input
                                        type="checkbox"
                                        checked={sel}
                                        onChange={() => toggleMessageSelected(row.id, m.id)}
                                        className="rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                                        aria-label={t("microsoft.ai_mailboxes.process_select_row")}
                                      />
                                    </td>
                                    <td className="whitespace-nowrap py-1.5 pr-2 align-top">
                                      {formatDate(m.received_at)}
                                    </td>
                                    <td className="max-w-[140px] truncate py-1.5 pr-2 align-top">
                                      {m.from_email || m.from_name || "—"}
                                    </td>
                                    <td className="py-1.5 align-top">
                                      {m.is_read === false && (
                                        <span className="mr-1 inline-block h-2 w-2 rounded-full bg-sky-500 align-middle" />
                                      )}
                                      {m.subject}
                                    </td>
                                    <td className="py-1.5 pl-1 align-top text-right">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setReaderTarget({
                                            mailboxId: row.id,
                                            mailboxEmail: row.email_address || "",
                                            graphMessageId: m.id,
                                            subject: m.subject || "",
                                          })
                                        }
                                        className="inline-flex rounded-md border border-sky-200 bg-sky-50 p-1 text-sky-900 hover:bg-sky-100"
                                        title={t("microsoft.ai_mailboxes.reader.open_tooltip")}
                                      >
                                        <EnvelopeOpenIcon className="h-4 w-4" aria-hidden />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
    </>
  );
}
